/**
 * Live 5-bar waveform rendered under the bird while push-to-talk is
 * active. Port of `BlueCursorWaveformView` in `OverlayWindow.swift:1190–1224`.
 *
 *   bar heights: base 3 + reactive component + idle sine pulse
 *   reactive:    clamp(amplitude * 2.85, 1) ^ 0.76, scaled by per-bar profile
 *   profile:     [0.4, 0.7, 1.0, 0.7, 0.4]  (center bar tallest)
 *   idle pulse:  ±1.5 pt sine wave
 *   bars:        2 pt wide, 2 pt spacing, corner radius 1.5 pt
 *   color:       DS.Colors.overlayCursorRed (#ff3b3b) with soft glow
 *   cadence:     36 fps redraw, 0.08 s amplitude smoothing
 *
 * The amplitude source is an `AnalyserNode` from `VoiceCaptureClient`'s
 * mic graph — Web Audio gives us the RMS-equivalent via
 * `getByteTimeDomainData` without any additional setup.
 */

import { SPRITE_SIZE_PX } from "./MovementController";

const BAR_COUNT = 5;
const BAR_WIDTH_PX = 2;
const BAR_SPACING_PX = 2;
const BAR_BASE_HEIGHT_PX = 3;
const BAR_CORNER_RADIUS_PX = 1.5;
const BAR_HEIGHT_PROFILE = [0.4, 0.7, 1.0, 0.7, 0.4] as const;
const REDRAW_INTERVAL_MS = 1000 / 36;
const VERTICAL_OFFSET_FROM_BIRD_CENTER = SPRITE_SIZE_PX / 2 + 10;
const AMPLITUDE_SMOOTHING = 0.08; // approximate SwiftUI 0.08 s easing

export interface WaveformOptions {
  getBirdCenter: () => { x: number; y: number };
}

export class Waveform {
  private readonly rootElement: HTMLDivElement;
  private readonly barElements: HTMLDivElement[];
  private readonly options: WaveformOptions;
  private analyser: AnalyserNode | null = null;
  // Explicit typing over an `ArrayBuffer` (not `ArrayBufferLike`) because
  // `AnalyserNode.getByteTimeDomainData` requires a non-shared buffer.
  private audioSampleBuffer: Uint8Array<ArrayBuffer> | null = null;
  private tickIntervalId: number | null = null;
  private smoothedAmplitude = 0;

  constructor(options: WaveformOptions) {
    this.options = options;
    this.rootElement = document.createElement("div");
    this.rootElement.className = "koko-waveform";

    this.barElements = Array.from({ length: BAR_COUNT }, () => {
      const bar = document.createElement("div");
      bar.className = "koko-waveform-bar";
      bar.style.width = `${BAR_WIDTH_PX}px`;
      bar.style.marginRight = `${BAR_SPACING_PX}px`;
      bar.style.borderRadius = `${BAR_CORNER_RADIUS_PX}px`;
      bar.style.height = `${BAR_BASE_HEIGHT_PX}px`;
      this.rootElement.appendChild(bar);
      return bar;
    });
    // Last bar has no trailing margin.
    this.barElements[BAR_COUNT - 1].style.marginRight = "0";
  }

  mount(container: HTMLElement): void {
    container.appendChild(this.rootElement);
  }

  setAnalyser(analyser: AnalyserNode | null): void {
    this.analyser = analyser;
    if (analyser) {
      // Allocate a dedicated ArrayBuffer so it isn't a shared/transferable
      // view — `getByteTimeDomainData` needs ArrayBuffer, not ArrayBufferLike.
      this.audioSampleBuffer = new Uint8Array(new ArrayBuffer(analyser.fftSize));
    } else {
      this.audioSampleBuffer = null;
    }
  }

  show(): void {
    this.rootElement.classList.add("visible");
    this.startTickLoop();
  }

  hide(): void {
    this.rootElement.classList.remove("visible");
    this.stopTickLoop();
    // Collapse bars back to base when hidden so re-show doesn't flash
    // the last sampled amplitude.
    this.smoothedAmplitude = 0;
    this.barElements.forEach((bar) => {
      bar.style.height = `${BAR_BASE_HEIGHT_PX}px`;
    });
  }

  destroy(): void {
    this.hide();
    this.rootElement.remove();
  }

  private startTickLoop(): void {
    if (this.tickIntervalId !== null) return;
    this.tickIntervalId = window.setInterval(() => this.tick(), REDRAW_INTERVAL_MS);
  }

  private stopTickLoop(): void {
    if (this.tickIntervalId !== null) {
      window.clearInterval(this.tickIntervalId);
      this.tickIntervalId = null;
    }
  }

  private tick(): void {
    this.updatePosition();
    const instantaneousAmplitude = this.sampleAmplitude();
    // 0.08 s smoothing: one-pole low-pass filter at ~36 fps.
    this.smoothedAmplitude =
      this.smoothedAmplitude +
      (instantaneousAmplitude - this.smoothedAmplitude) *
        Math.min(1, REDRAW_INTERVAL_MS / 1000 / AMPLITUDE_SMOOTHING);

    const reactiveComponent = Math.pow(
      Math.min(1, this.smoothedAmplitude * 2.85),
      0.76,
    );

    const idlePulseOffset = Math.sin(performance.now() / 250) * 1.5;

    this.barElements.forEach((bar, index) => {
      const profile = BAR_HEIGHT_PROFILE[index];
      // Native formula: base 3 + reactive * profile * 10.5 + idle pulse.
      // The 10.5 multiplier tops out at ~13.5 pt to match the native
      // 3–13.5 pt range described in OverlayWindow.swift:1216–1223.
      const height = BAR_BASE_HEIGHT_PX + reactiveComponent * profile * 10.5 + idlePulseOffset;
      bar.style.height = `${Math.max(BAR_BASE_HEIGHT_PX, height)}px`;
    });
  }

  private sampleAmplitude(): number {
    if (!this.analyser || !this.audioSampleBuffer) return 0;
    this.analyser.getByteTimeDomainData(this.audioSampleBuffer);

    // Convert the 8-bit time-domain samples (0–255, 128 = silence) into
    // an RMS-like magnitude in [0, 1].
    let sumOfSquares = 0;
    for (let i = 0; i < this.audioSampleBuffer.length; i++) {
      const deviationFromCenter = (this.audioSampleBuffer[i] - 128) / 128;
      sumOfSquares += deviationFromCenter * deviationFromCenter;
    }
    return Math.sqrt(sumOfSquares / this.audioSampleBuffer.length);
  }

  private updatePosition(): void {
    const birdCenter = this.options.getBirdCenter();
    const totalWidth = BAR_COUNT * BAR_WIDTH_PX + (BAR_COUNT - 1) * BAR_SPACING_PX;
    this.rootElement.style.left = `${birdCenter.x - totalWidth / 2}px`;
    this.rootElement.style.top = `${birdCenter.y + VERTICAL_OFFSET_FROM_BIRD_CENTER}px`;
  }
}
